import { Emitter } from './emitter.js'
import { deliverLetter } from './inbox.js'
import { ROOM_TYPE } from '@pine/protocol'

/**
 * One-to-one chats.
 *
 * The old arrangement made this a live negotiation: create a room, mint an
 * invite, open a channel to the other person's inbox, and wait up to thirty
 * seconds for it to land. If they were not online in that window it failed —
 * and because every attempt created its own room first, a person pressing the
 * button again because nothing had happened ended up with a list of empty
 * chats, one per attempt.
 *
 * Two rules replace it.
 *
 * The chat with a given person is *one* room. It is found by their account id
 * on the room summary, so starting a chat with somebody you already have one
 * with opens it instead of making another. Pressing the button twenty times is
 * indistinguishable from pressing it once.
 *
 * And nothing blocks on the other side being awake. The room exists locally
 * the moment you ask for it; the invite that lets them in goes into an outbox
 * that survives restarts and is retried whenever they might be reachable —
 * on connections, on a slow timer, at every launch — until they are in the
 * room. "They are offline" stops being a failure and becomes a state the
 * chat is in, which is what it always was.
 */

const RETRY_MIN = 15 * 1000
const RETRY_MAX = 2 * 60 * 1000
const DELIVERY_TIMEOUT = 20 * 1000

/*
 * A letter that has landed is sent again, now and then, until the person is
 * actually in the chat.
 *
 * "Landed" only ever meant that *a* device of theirs took it: the phone in a
 * pocket rather than the laptop being looked at, or a web app the system
 * reloaded a minute later. The outbox used to close on the first delivery,
 * and a request that went to the wrong screen was never seen at all. The
 * receiving side ignores copies of a request it already shows, and ones it
 * was told to decline.
 */
const REDELIVER_EVERY = 3 * 60 * 1000
const GIVE_UP_AFTER = 7 * 24 * 60 * 60 * 1000

export class DirectMessages extends Emitter {
  constructor (client) {
    super()
    this.client = client
    this.pending = new Map() // account -> { account, roomId, invite, at, attempts }
    this.started = false

    this._timer = null
    this._flushing = null
    this._detach = null
    this._sending = new Set()
    this._delay = RETRY_MIN
  }

  /* ---- lifecycle ---- */

  async start () {
    if (this.started) return
    this.started = true

    for await (const node of this.client.local.createReadStream({
      gte: 'outbox/', lt: 'outbox0'
    })) {
      if (node.value && node.value.account) this.pending.set(node.value.account, node.value)
    }

    // A new connection is the one moment worth trying immediately: it is the
    // only evidence we get that somebody who was not reachable might now be.
    this._detach = this.client.transport.onConnection(() => this._flushSoon())

    // Settled the moment they are let in, not at the next look.
    this._offJoined = this.client.on('member-joined', (roomId, { account } = {}) => {
      const entry = this.pending.get(account)
      if (entry && entry.roomId === roomId) this._settle(account).catch(() => {})
    })

    // Back online, or back in the foreground: the person may have come back
    // while this device was away. Try at once, and stay eager for a while,
    // rather than sitting out a backoff that grew while nobody was looking.
    this._offStatus = this.client.transport.onStatus((status) => {
      if (status !== 'online') return
      this._delay = RETRY_MIN
      this._schedule()
      this._flushSoon()
    })
    this._schedule()
    if (this.pending.size) this._flushSoon()
  }

  async stop () {
    if (!this.started) return
    this.started = false
    if (this._timer) clearTimeout(this._timer)
    if (this._flushing) clearTimeout(this._flushing)
    if (this._detach) this._detach()
    if (this._offStatus) this._offStatus()
    if (this._offJoined) this._offJoined()
    this._offStatus = null
    this._offJoined = null
    this._timer = null
    this._flushing = null
    this._detach = null
  }

  /* ---- the one entry point ---- */

  /**
   * Open the chat with an account, creating it the first time.
   *
   * @returns { room, created, delivered } — `delivered` is false while the
   * invite is still queued, which is normal and not an error.
   */
  async open (accountId, { name = '', note = '' } = {}) {
    if (accountId === this.client.identity.id) {
      throw new Error('That is your own account. Use Saved messages for notes to yourself.')
    }

    // A chat with the assistant, however it is started, tells her which
    // language to greet in (see lib/assistant.js).
    const assistant = this.client.assistant
    if (!note && assistant && assistant.isAssistant(accountId)) note = assistant.note()

    const contact = await this.client.saveContact(accountId, name ? { name } : {})
    const existing = this.find(accountId)

    if (existing) {
      const room = await this.client.openRoom(existing.id)
      return { room, created: false, delivered: !this.pending.has(accountId) }
    }

    // Deliberately nameless.
    //
    // Room metadata is shared, so a name put here is the name *both* people
    // see — and the only name available at this point is the other person's.
    // They then opened the chat and were greeted by their own name where the
    // caller's should have been. A one-to-one chat has no one name: each side
    // shows the other, which is a question only a device can answer, from the
    // membership and its own contacts.
    const room = await this.client.createRoom({
      name: '',
      type: ROOM_TYPE.DM
    })

    await this.client._saveSummary({
      ...this.client.summaries.get(room.id),
      peer: accountId,
      name: contact.name || shortId(accountId)
    })

    // The invite is minted here and only here. It is single-use and belongs to
    // exactly one person, so there is nothing to revoke if they never come.
    const invite = await this.client.createInvite(room.id, { uses: 1 })
    await this._queue(accountId, room.id, invite.code, note)

    this._flushSoon()
    return { room, created: true, delivered: false }
  }

  /** The existing chat with an account, or null. */
  find (accountId) {
    // There can be two: an old chat this side kept, and a new one the other
    // person started after losing theirs (see client._keepRequest). The one
    // to open is the one they are actually in now — the newest.
    let found = null
    for (const summary of this.client.summaries.values()) {
      if (summary.type !== ROOM_TYPE.DM || summary.peer !== accountId || summary.deleted) continue
      if (!found || (summary.joinedAt || 0) > (found.joinedAt || 0)) found = summary
    }
    return found
  }

  /** Whether an invite is still waiting to reach somebody. */
  isPending (accountId) {
    return this.pending.has(accountId)
  }

  /** The invite link for a chat whose invite has not landed, for sharing by hand. */
  pendingInvite (accountId) {
    const entry = this.pending.get(accountId)
    return entry ? entry.invite : null
  }

  /* ---- the outbox ---- */

  async _queue (account, roomId, invite, note = '') {
    // `note` travels with the request; a bot reads it (the assistant learns
    // which language to greet in), a person's app shows nothing of it.
    const entry = { account, roomId, invite, note: String(note || '').slice(0, 200), at: Date.now(), attempts: 0 }
    this.pending.set(account, entry)
    await this.client.local.put('outbox/' + account, entry)
    this.emit('pending-changed')
    // The invite is delivered peer to peer, so the other person's app has to
    // be running to take it. Wake it.
    if (this.client.wake) this.client.wake([account], 'msg')
    return entry
  }

  async _settle (account) {
    if (!this.pending.delete(account)) return
    await this.client.local.del('outbox/' + account).catch(() => {})
    this.emit('pending-changed')
    this.emit('joined', account)
  }

  _flushSoon () {
    if (this._flushing) return
    this._flushing = setTimeout(() => {
      this._flushing = null
      this._flush().catch(() => {})
    }, 250)
    if (this._flushing.unref) this._flushing.unref()
  }

  _schedule () {
    if (!this.started) return
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => {
      this._flush().catch(() => {})
      this._delay = Math.min(RETRY_MAX, this._delay * 2)
      this._schedule()
    }, this._delay)
    if (this._timer.unref) this._timer.unref()
  }

  async _flush () {
    if (!this.started || !this.pending.size) return

    for (const entry of [...this.pending.values()]) {
      if (this._sending.has(entry.account)) continue

      // They may have joined through some other route — a link passed by hand,
      // or an earlier attempt that landed after we gave up waiting for it.
      if (await this._alreadyIn(entry)) {
        await this._settle(entry.account)
        continue
      }
      if (Date.now() - (entry.at || 0) > GIVE_UP_AFTER) {
        await this._settle(entry.account)
        continue
      }
      if (entry.deliveredAt && Date.now() - entry.deliveredAt < REDELIVER_EVERY) continue

      this._sending.add(entry.account)
      try {
        await deliverLetter(this.client, entry.account,
          { invite: entry.invite, note: entry.note || '' },
          { timeout: DELIVERY_TIMEOUT })
        const first = !entry.deliveredAt
        entry.deliveredAt = Date.now()
        await this.client.local.put('outbox/' + entry.account, entry).catch(() => {})
        if (first) this.emit('delivered', entry.account)
        this._delay = RETRY_MIN // somebody was reachable; stay eager
      } catch {
        entry.attempts++
        await this.client.local.put('outbox/' + entry.account, entry).catch(() => {})
        // Nobody of theirs was listening. The one wake at the moment the chat
        // was started was all they ever got, and a phone that missed it never
        // heard of the request; each attempt that finds nobody asks again
        // (the server keeps that to one every fifteen seconds).
        if (this.client.wake) this.client.wake([entry.account], 'msg')
      } finally {
        this._sending.delete(entry.account)
      }
    }
  }

  async _alreadyIn (entry) {
    try {
      const room = await this.client.openRoom(entry.roomId)
      return await room.view.isMember(entry.account)
    } catch {
      return false // room gone: the next flush will drop it via the summary check
    }
  }
}

function shortId (account) {
  return account.slice(0, 6) + '…' + account.slice(-4)
}
